import React, { createContext, useContext, useMemo } from 'react';
import { ResourceArticleTiny, ResourceContentfulModel } from 'src/api/models/Resources';
import calculateEstimatedReadingTime from 'src/functions/calculateEstimatedReadingTime';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { Attachments, MarkupElement, MarkupValue } from 'src/functions/formatElement.htmlAst';
import { getTableOfContentDictionary, TableOfContent } from 'src/functions/getTableOfContentDictionary';

interface HeroBanner {
  title: string;
  description?: string;
  category?: string;
  author?: string;
  updatedAt?: string;
  readTime: string;
  image?: { gatsbyImageData: IGatsbyImageData; file?: { url: string } };
}

interface ResourceCenterArticleContext {
  article: ResourceContentfulModel;
  otherArticles: ResourceArticleTiny[];
  hasTableOfContents: boolean;
}

interface ResourceCenterArticleResponse extends ResourceCenterArticleContext {
  title: string;
  slug: string;
  heroBanner: HeroBanner;
  contentHtmlAst: MarkupElement[];
  attachments: Attachments;
  tableOfContents: TableOfContent[];
}

interface ResourceCenterArticleProviderProps extends ResourceCenterArticleContext {
  children: React.ReactNode;
}

const ResourceCenterArticleContext = createContext<ResourceCenterArticleContext>({
  article: {} as ResourceContentfulModel,
  otherArticles: [],
  hasTableOfContents: false,
});

export const useResourceCenterArticle = (): ResourceCenterArticleResponse => {
  const context = useContext(ResourceCenterArticleContext);

  if (!context) {
    throw new Error('useResourceCenterArticle must be used within a ResourceCenterArticleProvider');
  }

  const getHeroBanner = () => {
    const bodyWords = context.article.body.childMarkdownRemark?.wordCount?.words || 0;
    const bodyOverflowWords = context.article.bodyOverflow?.childMarkdownRemark?.wordCount?.words || 0;

    const readTime = calculateEstimatedReadingTime(bodyWords + bodyOverflowWords);

    return {
      title: context.article.title,
      description: context.article.description?.description,
      category: context.article.category,
      author: context.article.author,
      updatedAt: context.article.updatedAt,
      readTime: readTime,
      image: context.article.heroImage,
    };
  };

  // NOTE: Temporary fix to remove the "in this article..." and "((toc))" texts
  // Articles should have this body text fixed (e.g. e-learning-platforms)
  const _removeInThisArticleText = (articleData: MarkupElement[]) => {
    articleData.forEach((mE: MarkupElement) => {
      const recursiveDigIntoElement = (element: MarkupValue | MarkupElement, parent: MarkupElement): MarkupValue => {
        if (
          element.type === 'text' &&
          ['in this article…', 'in this article...'].includes(element.value.toLocaleLowerCase())
        ) {
          parent.children.splice(parent.children.indexOf(element), 1);
          return;
        }

        if (!(element as MarkupElement).children) return;

        (element as MarkupElement).children.forEach((childrenElement) =>
          recursiveDigIntoElement(childrenElement, element as MarkupElement)
        );
      };

      if (!mE.children) return;

      mE.children.forEach((childrenElement) => recursiveDigIntoElement(childrenElement, mE as MarkupElement));
    });
  };

  // NOTE: A temporary fix to remove styled CTA elements defined in body's markup
  // Articles should have this body text fixed ASAP
  const _removeForcedStyleCtaElement = (articleData: MarkupElement[]) => {
    const styleCtaElementIndex = articleData.findIndex(
      (mE: MarkupElement) => mE.properties?.style && mE.tagName === 'p'
    );

    if (styleCtaElementIndex > 0) {
      return articleData.filter((_, i) => i < styleCtaElementIndex);
    }
    return articleData;
  };

  const contentHtmlAst = useMemo(() => {
    const articleData = [
      ...(context.article.body.childMarkdownRemark?.htmlAst?.children || []),
      ...(context.article.bodyOverflow?.childMarkdownRemark?.htmlAst?.children || []),
    ];

    // These temporary fixes should be removed once the articles are fixed
    _removeInThisArticleText(articleData);
    _removeForcedStyleCtaElement(articleData);

    return articleData;
  }, [context.article]);

  const tableOfContents = useMemo(() => {
    const tocData = getTableOfContentDictionary(contentHtmlAst);
    return tocData;
  }, [contentHtmlAst]);

  return {
    title: context.article.title,
    slug: context.article.slug,
    article: context.article,
    otherArticles: context.otherArticles,
    hasTableOfContents: context.hasTableOfContents,
    heroBanner: getHeroBanner(),
    contentHtmlAst,
    tableOfContents,
    attachments: context.article.attachments,
  };
};

export const ResourceCenterArticleProvider: React.FC<ResourceCenterArticleProviderProps> = ({
  children,
  article,
  otherArticles,
  hasTableOfContents,
}) => {
  return (
    <ResourceCenterArticleContext.Provider value={{ article, otherArticles, hasTableOfContents }}>
      {children}
    </ResourceCenterArticleContext.Provider>
  );
};
